'use client';

import type { ReactNode, ReactElement } from 'react';
import { tv } from '../_lib/utils';
import { isValidRichText } from '../_lib/isValidRichText';
import { Anchor } from '../Anchor';
import { Media } from '../Media';
import { Popup } from '../Popup';
import { Text } from '../Text';
import ArrowUpRightIcon from '../_assets/icons/arrowUpRight.svg';
import type { SanityImage } from '../types';

const styles = tv({
	slots: {
		base: 'group bg-card-background-tertiary relative p-2 grid gap-3 rounded-lg border border-border-secondary overflow-hidden w-full h-full transition-all duration-normal ease-smooth hover:bg-card-background-tertiary-hover hover:-translate-y-1',
		media:
			'rounded-md overflow-hidden [&_img]:transition-transform [&_img]:duration-normal [&_img]:ease-smooth [&_img]:h-full relative z-10 [&_img]:group-hover:scale-105',
		copy: 'opacity-80',
	},
	variants: {
		size: {
			'2xl': {},
			xl: {},
			lg: {},
			md: {},
			sm: {},
		},
		layout: {
			horizontal: {
				base: 'md:gap-0',
				media: 'aspect-square w-[-webkit-fill-available] h-full',
			},
			vertical: {
				media: 'aspect-3/2',
			},
		},
		ellipsisContent: {
			true: {
				copy: 'overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:3] [-webkit-box-orient:vertical]',
			},
		},
	},
});

const innerStyles = tv({
	slots: {
		base: '',
	},
	variants: {
		size: {
			'2xl': {},
			xl: {},
			lg: {},
			md: {},
			sm: {},
		},
		layout: {
			vertical: {
				base: 'flex flex-col',
			},
			horizontal: {
				base: 'grid grid-cols-[4.5rem_1fr]',
			},
		},
	},
	compoundVariants: [
		{
			layout: 'horizontal',
			size: '2xl',
			className: 'sm:grid-cols-[7.5rem_1fr] lg:grid-cols-[13.75rem_1fr]',
		},
		{
			layout: 'horizontal',
			size: 'xl',
			className: 'sm:grid-cols-[7.5rem_1fr] lg:grid-cols-[13.75rem_1fr]',
		},
		{
			layout: 'horizontal',
			size: 'lg',
			className: 'sm:grid-cols-[7.5rem_1fr] lg:grid-cols-[11.25rem_1fr]',
		},
		{
			layout: 'horizontal',
			size: 'md',
			className: 'sm:grid-cols-[7.5rem_1fr]',
		},
	],
});

const contentStyles = tv({
	slots: {
		base: 'px-3 pt-4 pb-2 flex flex-col gap-3',
	},
	variants: {
		size: {
			'2xl': {},
			xl: {},
			lg: {},
			md: {},
			sm: {},
		},
		layout: {
			vertical: {
				base: 'grow flex flex-col justify-between',
			},
			horizontal: {
				base: 'pt-3 pr-3 pb-2 pl-4',
			},
		},
	},
	compoundVariants: [
		{
			layout: 'horizontal',
			size: 'md',
			className: 'pt-1 pr-0 pb-0',
		},
		{
			layout: 'horizontal',
			size: 'sm',
			className: 'pt-1 pr-0 pb-0',
		},
	],
});

const metaStyles = tv({
	slots: {
		base: 'flex items-center justify-between mt-auto',
	},
	variants: {
		size: {
			'2xl': {},
			xl: {},
			lg: {},
			md: {},
			sm: {},
		},
		layout: {
			vertical: {},
			horizontal: {},
		},
	},
	compoundVariants: [
		{
			layout: 'horizontal',
			size: 'sm',
			className: '-mt-2',
		},
	],
});

const iconStyles = tv({
	slots: {
		base: 'text-text-brand max-md:hidden md:translate-y-2 md:-translate-x-2 md:opacity-0 md:transition-[opacity_transform] md:duration-normal md:ease-smooth md:group-hover:opacity-100 md:group-hover:translate-y-0 md:group-hover:translate-x-0 md:h-6 md:w-6 md:ml-auto',
	},
	variants: {
		size: {
			'2xl': {},
			xl: {},
			lg: {},
			md: {},
			sm: {},
		},
		layout: {
			vertical: {},
			horizontal: {},
		},
	},
	compoundVariants: [
		{
			layout: 'horizontal',
			size: 'sm',
			className: 'hidden',
		},
	],
});

export type CardProps = {
	id?: string;
	copy?: ReactNode;
	className?: string;
	ellipsisContent?: boolean;
	image?: SanityImage;
	layout?: 'horizontal' | 'vertical';
	href?: string;
	overline?: string;
	popupContent?: ReactElement;
	title: ReactNode;
	video?: string;
	size?: '2xl' | 'xl' | 'lg' | 'md' | 'sm';
	target?: string;
	learnMore?: {
		learn: string;
		more: string;
	};
	name?: string;
	meta?: string[];
	nextImageSizes?: string;
};

export function Card(props: CardProps) {
	const layout = props.layout ?? 'vertical';
	const size = props.size ?? '2xl';
	const ellipsisContent = props.ellipsisContent ?? false;
	const { base, media, copy } = styles({ layout, ellipsisContent });
	const { base: inner } = innerStyles({ layout, size });
	const { base: content } = contentStyles({ layout, size });
	const { base: meta } = metaStyles({ layout, size });
	const { base: icon } = iconStyles({ layout, size });

	return (
		<article className={base({ className: props.className })} data-component='Card'>
			<div className={inner()}>
				<Media
					className={media()}
					image={props.image}
					video={props.video}
					aspectRatio={layout === 'horizontal' ? '1/1' : '3/2'}
					nextImageSizes={props.nextImageSizes}
				/>
				<div className={content()}>
					<div className='grid gap-2 z-10'>
						{props.overline && (
							<Text as='span' className={`text-text-brand`} styleType='label-sm'>
								{props.overline}
							</Text>
						)}
						{props.href ? (
							<Anchor
								className={`before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute before:content[""] before:z-10`}
								href={props.href}
								target={props.target}
							>
								<Text as='h2' styleType={getTitleSize(size, layout)}>
									{props.title}
								</Text>
							</Anchor>
						) : props.popupContent ? (
							<Popup
								layout={'sidebar'}
								triggerButton={
									<button
										className={`before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute before:content[""] before:z-10 text-left`}
									>
										<Text as='h2' styleType={getTitleSize(size, layout)}>
											{props.title}
										</Text>
									</button>
								}
							>
								{props.popupContent}
							</Popup>
						) : (
							<Text as='h2' styleType={getTitleSize(size, layout)}>
								{props.title}
							</Text>
						)}
						{isValidRichText(props.copy) && (
							<Text className={copy()} styleType='body-sm'>
								{props.copy}
							</Text>
						)}
					</div>
					<div className={meta()}>
						{props.meta && props.meta.length > 0 && (
							<ul className={`flex flex-wrap`}>
								{props.meta.map((item, index) => {
									return (
										<Text
											className={`py-1 opacity-80 flex items-center first:opacity-100 first:after:opacity-80 last:after:hidden after:content-[""] after:bg-current after:opacity-80 after:mx-1.5 after:h-[0.1875rem] after:w-[0.1875rem] after:rounded-full`}
											key={`card-meta-${index}`}
											as='li'
											styleType={getMetaSize(size, layout)}
										>
											{item}
										</Text>
									);
								})}
							</ul>
						)}
						<ArrowUpRightIcon className={icon()} />
					</div>
				</div>
			</div>
		</article>
	);
}

function getTitleSize(size: CardProps['size'], layout: CardProps['layout']) {
	if (layout === 'vertical') {
		switch (size) {
			case '2xl':
				return 'heading-xs';
			case 'xl':
				return 'heading-2xs';
			case 'lg':
				return 'heading-3xs';
			case 'md':
				return 'heading-4xs';
			case 'sm':
				return 'heading-5xs';
			default:
				return 'heading-3xs';
		}
	} else {
		switch (size) {
			case '2xl':
				return 'heading-2xs';
			case 'xl':
				return 'heading-3xs';
			case 'lg':
				return 'heading-5xs';
			case 'md':
				return 'heading-5xs';
			case 'sm':
				return 'heading-6xs';
			default:
				return 'heading-5xs';
		}
	}
}

function getMetaSize(size: CardProps['size'], layout: CardProps['layout']) {
	if (layout === 'vertical') {
		switch (size) {
			case '2xl':
				return 'chip-lg';
			case 'xl':
				return 'chip-lg';
			case 'lg':
				return 'chip-md';
			case 'md':
				return 'chip-md';
			case 'sm':
				return 'chip-md';
			default:
				return 'chip-md';
		}
	} else {
		return 'chip-md';
	}
}
